import {AbstractControl, FormControl, ValidatorFn} from '@angular/forms';
import * as moment from 'moment';
import {DateService} from '@services/date.service';

const requiredMessage = 'Поле обязательно для заполнения';

/**
 * Метод разрешения валидации.
 * Возвращает false, если это поле валидировать не стоит (например, когда с ним еще не работали)
 * @param {AbstractControl} input
 * @returns {boolean}
 */
const allowValidation = (input: AbstractControl): boolean => {
    return input.dirty && input.touched && !input.disabled;
};

/**
 * Валидация поля на фронте на обязательность заполнения
 * @param input
 * @returns {{frontendRequired: string}}
 */
export function frontendRequiredValidator(input: FormControl) {
    return !input.value && allowValidation(input) ? {'frontendRequired': requiredMessage} : null;
}

/**
 * Значение должно быть True
 * @param input
 */
export function frontendRequiredTrue(input: FormControl) {
    return allowValidation(input) && input.value !== true ? {'frontendRequiredTrue': 'Значение не установлено'} : null;
}

/**
 * Поле обязательно для заполнения с кастомным текстом ошибок валидации
 * @param {string} errorMessage - текст ошибки валидации
 * @returns {(input: FormControl) => {frontendRequired: string}}
 */
export function requiredWithMessage(errorMessage: string) {
    return (input: FormControl) => {
        return !input.value && allowValidation(input) ? {'requiredWithMessage': errorMessage} : null;
    };
}

/**
 * Валидация на корректности заполнения поля с именем fieldName количеством символов length, за исключением
 * спецсимволов маски ввода "_"
 * @param {number} length
 * @param {string} message
 * @returns {(input: FormControl) => ({required: string} | {requiredLengthExceptInputmask: string})}
 */
export function requiredLengthExceptInputmask(length: number, message: string) {
    return (input: FormControl) => {
        /* Если с полем еще не работали, то нафиг его валидировать */
        if (!allowValidation(input)) {
            return null;
        }

        /**
         * Проверка на обязательность не в рамкаж этой валидации
         */
        if (!input.value) {
            return null;
        }

        /**
         * Если пусто, не до конца заполнено и есть символы маски ввода, то ошибка
         */
        if (input.value.length !== length || input.value.indexOf('_') >= 0) {
            return {
                'requiredLengthExceptInputmask': message
            };
        }

        return null;
    };
}

/**
 * Проверка на спецсимволы в имени. Запрещает использолвание спецсимволов.
 * @param {string} messageSuffix
 * @param {string} chars
 * @returns {(input: FormControl) => {requiredLengthExceptInputmask: string}}
 */
export function disallowSpecialCharacters(messageSuffix: string = 'в наименовании недопустимы.', chars = '$?%^~&@#') {
    return (input: FormControl) => {
        /* Если с полем еще не работали, то нафиг его валидировать */
        if (!allowValidation(input)) {
            return null;
        }

        let foundedCharsCount = 0;

        for (let i = 0; i < chars.length; i++) {
            const char = chars.charAt(i);
            if (input.value && input.value.indexOf(char) >= 0) {
                foundedCharsCount++;
            }
        }

        if (!foundedCharsCount) {
            return null;
        }

        return {
            'disallowSpecialCharacters': `Спецсимволы ${chars} ${messageSuffix}`
        };
    };
}

/**
 * Проверка на то, что дата введена полностью
 * @param {string} format
 * @returns {(input: FormControl) => (null | {fullDateValidation: string} | {validDateValidator: string})}
 */
export function fullDateValidation(format: string = 'dd.mm.yyyy') {
    return (input: FormControl) => {
        /* Если с полем еще не работали, то нафиг его валидировать */
        if (!allowValidation(input)) {
            return null;
        }

        /**
         * Проверка на обязательность не в рамкаж этой валидации
         */
        if (!input.value) {
            return null;
        }

        /**
         * Длина строки формата __.__.____ равна 10 символам
         */
        if (input.value.indexOf('_') >= 0) {
            return {
                'fullDateValidation': `Введенные данные не полные, укажите полную дату.`
            };
        }

        /**
         * Проверяем на валидность заполненную дату
         * @type {moment.Moment}
         */
        const date = moment(input.value, format.toUpperCase());

        return date.isValid() ? null : {
            validDateValidator: 'Введенные данные некорректны, выберите или укажите правильную дату.'
        };
    };
}

/**
 * Валидация связки диапазона дат, чтобы дата начала не была позже даты окончания или дата окончания не была раньше даты начала,
 * в зависимости от того, навешивается валидатор на поле с датой начала и датой окончания.
 * @param compareTo - Поле, с которым будет сравниваться текущее
 * @param isStart - является ли текущее поле датой начала (true) или датой окончания (false)
 */
export function startEndDateValidation(compareTo: FormControl, isStart = true): ValidatorFn {
    return (target: FormControl) => {

        if (!compareTo.valid || !target.valid || !compareTo.value || !target.value) {
            return null;
        }

        const compareToDate = DateService.formatedValueToDate(compareTo.value);
        const targetDate = DateService.formatedValueToDate(target.value);

        if (!compareToDate || !targetDate) {
            return null;
        }

        if (isStart) {
            if (targetDate.getTime() > compareToDate.getTime()) {
                return { startEnd: 'Дата начала не может быть больше даты окончания' };
            } else {
                return null;
            }
        } else {
            if (targetDate.getTime() < compareToDate.getTime()) {
                return { startEnd: 'Дата окончания не может быть меньше даты начала' };
            } else {
                return null;
            }
        }
    };
};

export function emailValidation(input: FormControl) {
    /* Если с полем еще не работали, то нафиг его валидировать */
    if (!allowValidation(input)) {
        return null;
    }

    /**
     * Проверка на обязательность не в рамкаж этой валидации
     */
    if (!input.value) {
        return null;
    }

    /**
     * TODO упростить проверку. В иделае вызвать ангуляровский валидтор, но со своим сообщением об ошибке
     * Регулярка отсюда https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
     * @type {RegExp}
     */
    /* tslint:disable:max-line-length */
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    /* tslint:enable:max-line-length */

    return !re.test(input.value.toLowerCase()) ? {
        'invalidEmail': 'Неверный формат электронной почты. Исправьте адрес электронной почты и попробуйте ещё раз.'
    } : null;
}
